if (!customElements.get("famous-star-video-carousel")) {
  customElements.define(
    "famous-star-video-carousel",
    class FamousStarVideoCarousel extends HTMLElement {
      connectedCallback() {
        this.sliderEl = this.querySelector(".famous-star-video__slider");
        if (!this.sliderEl) return;

        this.sectionId = this.dataset.sectionId;
        this.sectionRoot =
          (this.sectionId &&
            document.getElementById(`shopify-section-${this.sectionId}`)) ||
          this.closest(".shopify-section");

        const desktopNav = this.sectionRoot?.querySelector(
          ".famous-star-video__nav--desktop",
        );
        this.desktopPrevEl =
          desktopNav?.querySelector(".swiper-button-prev") ?? null;
        this.desktopNextEl =
          desktopNav?.querySelector(".swiper-button-next") ?? null;

        const mobileNav = this.querySelector(".famous-star-video__nav--mobile");
        this.mobilePrevEl =
          mobileNav?.querySelector(".swiper-button-prev") ?? null;
        this.mobileNextEl =
          mobileNav?.querySelector(".swiper-button-next") ?? null;

        this.prevEl = this.desktopPrevEl || this.mobilePrevEl;
        this.nextEl = this.desktopNextEl || this.mobileNextEl;
        this.slideCount =
          this.sliderEl.querySelectorAll(".swiper-slide").length;

        this.spaceBetweenBase =
          parseInt(this.dataset.spaceBetween || "16", 10) || 16;
        this.sliderInstance = null;
        this.playHandlers = [];
        this.playerClickHandlers = [];
        this._navAbort = new AbortController();

        this.initSlider();
        this.bindPlayButtons();
        this.initMobileCoverOverlay();
      }

      isMobileUi() {
        return window.matchMedia("(max-width: 767px)").matches;
      }

      clearMobileCoverTimer(card) {
        if (card._fsvCoverTimer) {
          clearTimeout(card._fsvCoverTimer);
          card._fsvCoverTimer = null;
        }
      }

      scheduleMobileCoverAutoHide(card) {
        if (!this.isMobileUi()) return;
        if (card.classList.contains("is-playing")) return;
        this.clearMobileCoverTimer(card);
        card.classList.remove("fsv-mobile-overlay-auto-hidden");
        card._fsvCoverTimer = setTimeout(() => {
          card._fsvCoverTimer = null;
          if (!card.classList.contains("is-playing")) {
            card.classList.add("fsv-mobile-overlay-auto-hidden");
          }
        }, 3000);
      }

      suppressPlayButtonFocusWhilePlaying(card) {
        const playBtn = card.querySelector(".famous-star-video__play");
        if (!playBtn) return;
        playBtn.blur();
        playBtn.setAttribute("tabindex", "-1");
      }

      restorePlayButtonFocusability(card) {
        const playBtn = card.querySelector(".famous-star-video__play");
        if (!playBtn) return;
        playBtn.removeAttribute("tabindex");
      }

      initMobileCoverOverlay() {
        if (!this.isMobileUi()) return;
        this.querySelectorAll(".famous-star-video__card").forEach((card) => {
          if (!card.classList.contains("is-playing")) {
            this.scheduleMobileCoverAutoHide(card);
          }
        });
      }

      clearMobileUiState(card) {
        if (card._fsvUiTimer) {
          clearTimeout(card._fsvUiTimer);
          card._fsvUiTimer = null;
        }
        card.classList.remove("fsv-ui-expanded");
        const player = card.querySelector(".famous-star-video__player");
        if (player) {
          const cap = player.querySelector(".famous-star-video__tap-capture");
          if (cap) {
            cap.remove();
          }
        }
        const v = card.querySelector(".famous-star-video__player video");
        if (v) {
          v.removeAttribute("controls");
          v.style.pointerEvents = "";
        }
      }

      removeTapCapture(player) {
        const cap = player.querySelector(".famous-star-video__tap-capture");
        if (cap) cap.remove();
      }

      addTapCapture(player, card) {
        this.removeTapCapture(player);
        const cap = document.createElement("div");
        cap.className = "famous-star-video__tap-capture";
        cap.setAttribute("data-fsv-capture", "");
        cap.setAttribute("aria-hidden", "true");
        const onActivate = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.expandMobilePlaybackUI(card, player);
        };
        cap.addEventListener("click", onActivate);
        player.appendChild(cap);
      }

      scheduleMobileUiCollapse(card, player, delayMs) {
        if (card._fsvUiTimer) {
          clearTimeout(card._fsvUiTimer);
          card._fsvUiTimer = null;
        }
        card._fsvUiTimer = setTimeout(() => {
          card._fsvUiTimer = null;
          this.collapseMobilePlaybackUI(card, player);
        }, delayMs);
      }

      collapseMobilePlaybackUI(card, player) {
        if (!card.classList.contains("is-playing") || !this.isMobileUi()) return;
        card.classList.remove("fsv-ui-expanded");
        const video = player.querySelector("video");
        if (video) {
          video.removeAttribute("controls");
          video.style.pointerEvents = "none";
        }
        if (player.querySelector("iframe")) {
          this.addTapCapture(player, card);
        }
      }

      expandMobilePlaybackUI(card, player) {
        if (!card.classList.contains("is-playing") || !this.isMobileUi()) return;
        card.classList.add("fsv-ui-expanded");
        this.removeTapCapture(player);
        const video = player.querySelector("video");
        if (video) {
          video.setAttribute("controls", "");
          video.style.pointerEvents = "";
        }
        /* 每次展开（含再次点击视频区）均显示约 3 秒后收起 */
        this.scheduleMobileUiCollapse(card, player, 3000);
      }

      setupMobilePlaybackUI(card, player) {
        this.expandMobilePlaybackUI(card, player);
      }

      getSpaceBetweenPx(viewportWidth) {
        const w = viewportWidth ?? window.innerWidth;
        const base = this.spaceBetweenBase;
        if (w < 768) {
          /* 375 设计基准：卡片间距 8px，随视口同比缩放 */
          return Math.max(0, Math.round((8 * w) / 375));
        }
        return Math.min(base, Math.round((base * Math.min(w, 1920)) / 1920));
      }

      initSlider() {
        if (this.sliderInstance || this.slideCount < 1) return;

        const vw = window.innerWidth;
        const spaceBetween = this.getSpaceBetweenPx(vw);
        const centeredSlides = vw < 768 && this.slideCount > 1;

        const sliderOptions = {
          modules: [FoxTheme.Swiper.Navigation, FoxTheme.Swiper.Mousewheel],
          slidesPerView: "auto",
          spaceBetween,
          centeredSlides,
          centeredSlidesBounds: centeredSlides,
          centerInsufficientSlides: centeredSlides,
          loop: false,
          rewind: false,
          grabCursor: this.slideCount > 1,
          allowTouchMove: this.slideCount > 1,
          watchOverflow: true,
          threshold: 5,
          mousewheel: {
            enabled: this.slideCount > 1,
            forceToAxis: true,
          },
          keyboard: {
            enabled: this.slideCount > 1,
            onlyInViewport: true,
          },
        };

        if (this.prevEl && this.nextEl) {
          sliderOptions.navigation = {
            nextEl: this.nextEl,
            prevEl: this.prevEl,
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(
          this.sliderEl,
          sliderOptions,
        );
        this.sliderInstance.init();
        if (this.sliderInstance?.slider) {
          const slider = this.sliderInstance.slider;
          slider.on("slideChangeTransitionStart", () => this.stopAllVideos());
          slider.on("slideChange", () => this.syncNavDisabled());
          slider.on("resize", () => this.syncNavDisabled());
          this.syncNavDisabled();
        }

        this._onWindowResize = () => {
          this.syncSpaceBetween();
          this.syncNavDisabled();
          if (!this.isMobileUi()) {
            this.querySelectorAll(".famous-star-video__card").forEach((card) => {
              this.clearMobileCoverTimer(card);
              card.classList.remove("fsv-mobile-overlay-auto-hidden");
            });
          } else {
            this.querySelectorAll(".famous-star-video__card").forEach((card) => {
              if (!card.classList.contains("is-playing")) {
                this.scheduleMobileCoverAutoHide(card);
              }
            });
          }
        };
        window.addEventListener("resize", this._onWindowResize);

        this.bindMobileNavClicks();

        if (
          typeof Shopify !== "undefined" &&
          Shopify.designMode &&
          this.sectionId
        ) {
          this._onBlockSelect = (e) => {
            if (e.detail.sectionId !== this.sectionId) return;
            const target = e.target;
            const idx = target?.dataset?.index;
            if (idx !== undefined && this.sliderInstance?.slider) {
              this.sliderInstance.slider.slideTo(parseInt(idx, 10));
            }
          };
          document.addEventListener(
            "shopify:block:select",
            this._onBlockSelect,
          );
        }
      }

      syncSpaceBetween() {
        const slider = this.sliderInstance?.slider;
        if (!slider) return;
        const w = window.innerWidth;
        const gap = this.getSpaceBetweenPx(w);
        const centered = w < 768 && this.slideCount > 1;
        slider.params.spaceBetween = gap;
        slider.params.centeredSlides = centered;
        slider.params.centeredSlidesBounds = centered;
        slider.params.centerInsufficientSlides = centered;
        slider.update();
      }

      syncNavDisabled() {
        const slider = this.sliderInstance?.slider;
        if (!slider || this.slideCount <= 1) return;

        const locked = slider.isLocked === true;
        const atStart = slider.isBeginning || locked;
        const atEnd = slider.isEnd || locked;

        const setPrev = (el) => {
          if (!el) return;
          el.classList.toggle("swiper-button-disabled", atStart);
          if (atStart) el.setAttribute("disabled", "");
          else el.removeAttribute("disabled");
        };
        const setNext = (el) => {
          if (!el) return;
          el.classList.toggle("swiper-button-disabled", atEnd);
          if (atEnd) el.setAttribute("disabled", "");
          else el.removeAttribute("disabled");
        };

        setPrev(this.desktopPrevEl);
        setPrev(this.mobilePrevEl);
        setNext(this.desktopNextEl);
        setNext(this.mobileNextEl);
      }

      bindMobileNavClicks() {
        const slider = this.sliderInstance?.slider;
        const { signal } = this._navAbort || {};
        if (!slider || this.slideCount <= 1 || !signal) return;

        const goPrev = (e) => {
          e.preventDefault();
          slider.slidePrev();
        };
        const goNext = (e) => {
          e.preventDefault();
          slider.slideNext();
        };

        if (
          this.mobilePrevEl &&
          this.desktopPrevEl &&
          this.mobilePrevEl !== this.desktopPrevEl
        ) {
          this.mobilePrevEl.addEventListener("click", goPrev, { signal });
        }
        if (
          this.mobileNextEl &&
          this.desktopNextEl &&
          this.mobileNextEl !== this.desktopNextEl
        ) {
          this.mobileNextEl.addEventListener("click", goNext, { signal });
        }
      }

      bindPlayButtons() {
        const cards = this.querySelectorAll(".famous-star-video__card");
        cards.forEach((card) => {
          const playBtn = card.querySelector(".famous-star-video__play");
          if (playBtn) {
            const handler = () => this.playCardVideo(card);
            playBtn.addEventListener("click", handler);
            this.playHandlers.push({ element: playBtn, handler });
          }
          const onCardClick = (e) => {
            if (!this.isMobileUi()) return;
            if (e.target.closest(".famous-star-video__play")) return;
            if (card.classList.contains("is-playing")) return;
            this.scheduleMobileCoverAutoHide(card);
          };
          card.addEventListener("click", onCardClick);
          this.playerClickHandlers.push({
            element: card,
            handler: onCardClick,
          });
          const player = card.querySelector(".famous-star-video__player");
          if (player) {
            const onPlayerClick = (e) => {
              if (!this.isMobileUi()) return;
              if (!card.classList.contains("is-playing")) return;
              e.stopPropagation();
              if (card.classList.contains("fsv-ui-expanded")) {
                this.scheduleMobileUiCollapse(card, player, 3000);
                return;
              }
              this.expandMobilePlaybackUI(card, player);
            };
            player.addEventListener("click", onPlayerClick);
            this.playerClickHandlers.push({ element: player, handler: onPlayerClick });
          }
        });
      }

      buildEmbedUrl(type, id) {
        if (!type || !id) return "";
        if (type === "youtube") {
          return `https://www.youtube.com/embed/${id}?autoplay=1&playsinline=1&rel=0&modestbranding=1`;
        }
        if (type === "vimeo") {
          return `https://player.vimeo.com/video/${id}?autoplay=1&muted=0&playsinline=1`;
        }
        return "";
      }

      extractVideoIdByUrl(url) {
        if (url == null || String(url).trim() === "")
          return { type: "", id: "" };
        let normalized = String(url).trim();
        if (!/^https?:\/\//i.test(normalized)) {
          normalized = `https://${normalized}`;
        }
        try {
          const parsed = new URL(normalized);
          const host = parsed.hostname.replace(/^www\./, "").toLowerCase();
          if (host === "youtu.be" || host.endsWith(".youtu.be")) {
            const segs = parsed.pathname.split("/").filter(Boolean);
            const id = segs[0] || "";
            return { type: "youtube", id };
          }
          if (host.includes("youtube.com")) {
            const fromQuery = parsed.searchParams.get("v");
            if (fromQuery) return { type: "youtube", id: fromQuery };
            const parts = parsed.pathname.split("/").filter(Boolean);
            const fromPath = parts[1];
            if (fromPath && ["shorts", "embed", "live"].includes(parts[0])) {
              return { type: "youtube", id: fromPath };
            }
            return { type: "youtube", id: "" };
          }
          if (host.includes("vimeo.com")) {
            const id = parsed.pathname.split("/").filter(Boolean)[0] || "";
            return { type: "vimeo", id };
          }
        } catch (error) {
          return { type: "", id: "" };
        }
        return { type: "", id: "" };
      }

      playCardVideo(card) {
        this.clearMobileCoverTimer(card);
        card.classList.remove("fsv-mobile-overlay-auto-hidden");

        const player = card.querySelector(".famous-star-video__player");
        if (!player) return;

        let type = player.dataset.videoType;
        let id = player.dataset.videoId;
        const fileUrl = player.dataset.fileUrl;
        const externalUrl = player.dataset.externalUrl;

        if ((!type || !id) && externalUrl) {
          const parsed = this.extractVideoIdByUrl(externalUrl);
          type = parsed.type;
          id = parsed.id;
        }

        this.stopAllVideos(card);

        if (fileUrl) {
          const video = document.createElement("video");
          video.src = fileUrl;
          if (!this.isMobileUi()) {
            video.controls = true;
          }
          video.autoplay = true;
          video.playsInline = true;
          video.setAttribute("preload", "metadata");
          player.appendChild(video);
        } else if (externalUrl && (!type || !id)) {
          const video = document.createElement("video");
          video.src = externalUrl;
          if (!this.isMobileUi()) {
            video.controls = true;
          }
          video.autoplay = true;
          video.playsInline = true;
          video.setAttribute("preload", "metadata");
          player.appendChild(video);
        } else {
          const embedUrl = this.buildEmbedUrl(type, id);
          if (!embedUrl) return;
          const iframe = document.createElement("iframe");
          iframe.src = embedUrl;
          iframe.allow =
            "autoplay; encrypted-media; picture-in-picture; fullscreen";
          iframe.setAttribute("allowfullscreen", "");
          iframe.setAttribute("title", "Video player");
          player.appendChild(iframe);
        }

        card.classList.add("is-playing");
        this.suppressPlayButtonFocusWhilePlaying(card);
        if (this.isMobileUi()) {
          this.setupMobilePlaybackUI(card, player);
        }
      }

      stopAllVideos(exceptCard = null) {
        const cards = this.querySelectorAll(".famous-star-video__card");
        cards.forEach((card) => {
          if (exceptCard && card === exceptCard) return;
          this.clearMobileUiState(card);
          card.classList.remove("is-playing");
          this.restorePlayButtonFocusability(card);
          const player = card.querySelector(".famous-star-video__player");
          if (player) player.innerHTML = "";
          if (this.isMobileUi()) {
            this.scheduleMobileCoverAutoHide(card);
          }
        });
      }

      disconnectedCallback() {
        if (this._onWindowResize) {
          window.removeEventListener("resize", this._onWindowResize);
          this._onWindowResize = null;
        }
        this._navAbort?.abort();
        if (
          typeof Shopify !== "undefined" &&
          Shopify.designMode &&
          this._onBlockSelect
        ) {
          document.removeEventListener(
            "shopify:block:select",
            this._onBlockSelect,
          );
        }
        this.playHandlers.forEach(({ element, handler }) =>
          element.removeEventListener("click", handler),
        );
        this.playHandlers = [];
        this.playerClickHandlers?.forEach(({ element, handler }) =>
          element.removeEventListener("click", handler),
        );
        this.playerClickHandlers = [];
        this.stopAllVideos();
        if (this.sliderInstance?.slider) {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = null;
        }
      }
    },
  );
}
